import { useTranslation } from 'react-i18next';
import React, { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArrowRight, ArrowLeft, Calculator, RotateCcw, Banknote, Loader2, ArrowRightLeft } from 'lucide-react';
import { toast } from 'sonner';
import { CopyNumberButton } from '../shared/CopyNumberButton';
import useRememberedInputs from '../../hooks/useRememberedInputs';
import { ResetFiltersButton } from '../shared/ResetFiltersButton';

// Supported currencies with their NBP table (A or B)
const CURRENCIES = [
  { code: 'EUR', name: 'Euro', table: 'A' },
  { code: 'USD', name: 'US Dollar', table: 'A' },
  { code: 'GBP', name: 'British Pound', table: 'A' },
  { code: 'CHF', name: 'Swiss Franc', table: 'A' },
  { code: 'DKK', name: 'Danish Krone', table: 'A' },
  { code: 'NOK', name: 'Norwegian Krone', table: 'A' },
  { code: 'SEK', name: 'Swedish Krona', table: 'A' },
  { code: 'CZK', name: 'Czech Koruna', table: 'A' },
  { code: 'HUF', name: 'Hungarian Forint', table: 'A' },
  { code: 'BGN', name: 'Bulgarian Lev', table: 'A' },
  { code: 'TRY', name: 'Turkish Lira', table: 'A' },
  { code: 'ILS', name: 'Israeli Shekel', table: 'A' },
  { code: 'UAH', name: 'Ukrainian Hryvnia', table: 'A' },
  { code: 'AUD', name: 'Australian Dollar', table: 'A' },
  { code: 'CAD', name: 'Canadian Dollar', table: 'A' },
  { code: 'JPY', name: 'Japanese Yen', table: 'A' },
  { code: 'CNY', name: 'Chinese Yuan', table: 'A' },
  { code: 'THB', name: 'Thai Baht', table: 'A' },
  { code: 'KRW', name: 'South Korean Won', table: 'A' },
  { code: 'XDR', name: 'SDR (IMF)', table: 'A' },
  { code: 'TWD', name: 'Taiwan Dollar', table: 'B' },
];

const TaxCalculator = () => {
  const { t, i18n } = useTranslation();
  // Tax rate state
  const [taxRate, setTaxRate] = useState('23');
  
  // Net to Gross calculator state
  const [netAmount, setNetAmount] = useState('');
  const [calculatedGross, setCalculatedGross] = useState(null);
  
  // Gross to Net calculator state
  const [grossAmount, setGrossAmount] = useState('');
  const [calculatedNet, setCalculatedNet] = useState(null);

  // Exchange rate state
  const [selectedCurrency, setSelectedCurrency] = useState('EUR');
  const [exchangeDate, setExchangeDate] = useState(new Date().toISOString().split('T')[0]);
  const [exchangeResult, setExchangeResult] = useState(null);
  const [exchangeLoading, setExchangeLoading] = useState(false);

  // Currency converter state
  const [convertAmount, setConvertAmount] = useState('');
  const [convertFrom, setConvertFrom] = useState('EUR');
  const [convertTo, setConvertTo] = useState('PLN');
  const [convertDate, setConvertDate] = useState(new Date().toISOString().split('T')[0]);
  const [convertResult, setConvertResult] = useState(null);
  const [convertLoading, setConvertLoading] = useState(false);
  useRememberedInputs('budget-calculate', {
    taxRate: [taxRate, setTaxRate], netAmount: [netAmount, setNetAmount], grossAmount: [grossAmount, setGrossAmount],
    selectedCurrency: [selectedCurrency, setSelectedCurrency], convertAmount: [convertAmount, setConvertAmount],
    convertFrom: [convertFrom, setConvertFrom], convertTo: [convertTo, setConvertTo],
  });
  const lang = (i18n.language || 'en').slice(0, 2);
  const currencyName = (c) => {
    try { return new Intl.DisplayNames([lang], { type: 'currency' }).of(c.code) || c.name; } catch { return c.name; }
  };

  // All currencies including PLN for converter
  const ALL_CURRENCIES = [{ code: 'PLN', name: 'Polish Zloty', table: null }, ...CURRENCIES];

  // Format number with 2 decimal places and thousand separators
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Format number with 4 decimal places for exchange rates
  const formatRate = (value) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 4,
      maximumFractionDigits: 4
    }).format(value);
  };

  // Calculate Gross from Net
  const calculateNetToGross = useCallback(() => {
    const net = parseFloat(netAmount.replace(',', '.'));
    const rate = parseFloat(taxRate.replace(',', '.'));
    
    if (isNaN(net) || isNaN(rate)) {
      setCalculatedGross(null);
      return;
    }
    
    const taxMultiplier = 1 + (rate / 100);
    const gross = net * taxMultiplier;
    const taxAmount = gross - net;
    
    setCalculatedGross({
      gross: gross,
      tax: taxAmount,
      net: net,
      rate: rate
    });
  }, [netAmount, taxRate]);

  // Calculate Net from Gross
  const calculateGrossToNet = useCallback(() => {
    const gross = parseFloat(grossAmount.replace(',', '.'));
    const rate = parseFloat(taxRate.replace(',', '.'));
    
    if (isNaN(gross) || isNaN(rate)) {
      setCalculatedNet(null);
      return;
    }
    
    const taxMultiplier = 1 + (rate / 100);
    const net = gross / taxMultiplier;
    const taxAmount = gross - net;
    
    setCalculatedNet({
      net: net,
      tax: taxAmount,
      gross: gross,
      rate: rate
    });
  }, [grossAmount, taxRate]);

  // Fetch NBP exchange rate
  const fetchExchangeRate = useCallback(async () => {
    setExchangeLoading(true);
    setExchangeResult(null);
    
    const currency = CURRENCIES.find(c => c.code === selectedCurrency);
    if (!currency) {
      toast.error(t('budget.currencyNotFound'));
      setExchangeLoading(false);
      return;
    }

    // Calculate the previous working day from selected date
    const selectedDateObj = new Date(exchangeDate);
    let searchDate = new Date(selectedDateObj);
    
    // Go back one day to get "previous working day" rate
    searchDate.setDate(searchDate.getDate() - 1);
    
    // Try to fetch rate, going back up to 7 days to find a working day
    let attempts = 0;
    const maxAttempts = 7;
    
    while (attempts < maxAttempts) {
      const dateStr = searchDate.toISOString().split('T')[0];
      const url = `https://api.nbp.pl/api/exchangerates/rates/${currency.table}/${selectedCurrency}/${dateStr}/?format=json`;
      
      try {
        const response = await fetch(url);
        
        if (response.ok) {
          const data = await response.json();
          const rate = data.rates[0];
          
          setExchangeResult({
            currency: selectedCurrency,
            currencyName: currency.name,
            rate: rate.mid,
            effectiveDate: rate.effectiveDate,
            no: rate.no,
            table: currency.table,
            requestedDate: exchangeDate
          });
          
          setExchangeLoading(false);
          return;
        } else if (response.status === 404) {
          // No rate for this date, try previous day
          searchDate.setDate(searchDate.getDate() - 1);
          attempts++;
        } else {
          throw new Error(`NBP API error: ${response.status}`);
        }
      } catch (error) {
        if (attempts >= maxAttempts - 1) {
          toast.error(t('budget.fetchRateFailed', { error: error.message }));
          setExchangeLoading(false);
          return;
        }
        searchDate.setDate(searchDate.getDate() - 1);
        attempts++;
      }
    }
    
    toast.error(t('budget.couldNotFindExchangeRateFor'));
    setExchangeLoading(false);
  }, [selectedCurrency, exchangeDate]);

  // Fetch rate for a specific currency (helper for converter)
  const fetchRateForCurrency = useCallback(async (currencyCode, date) => {
    if (currencyCode === 'PLN') {
      return { rate: 1, effectiveDate: date, no: 'N/A' };
    }

    const currency = CURRENCIES.find(c => c.code === currencyCode);
    if (!currency) return null;

    const selectedDateObj = new Date(date);
    let searchDate = new Date(selectedDateObj);
    searchDate.setDate(searchDate.getDate() - 1);

    let attempts = 0;
    const maxAttempts = 7;

    while (attempts < maxAttempts) {
      const dateStr = searchDate.toISOString().split('T')[0];
      const url = `https://api.nbp.pl/api/exchangerates/rates/${currency.table}/${currencyCode}/${dateStr}/?format=json`;

      try {
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          const rate = data.rates[0];
          return {
            rate: rate.mid,
            effectiveDate: rate.effectiveDate,
            no: rate.no
          };
        } else if (response.status === 404) {
          searchDate.setDate(searchDate.getDate() - 1);
          attempts++;
        } else {
          return null;
        }
      } catch (error) {
        searchDate.setDate(searchDate.getDate() - 1);
        attempts++;
      }
    }
    return null;
  }, []);

  // Convert currency
  const convertCurrency = useCallback(async () => {
    const amount = parseFloat(convertAmount.replace(',', '.'));
    if (isNaN(amount) || amount <= 0) {
      toast.error(t('budget.pleaseEnterAValidAmount'));
      return;
    }

    if (convertFrom === convertTo) {
      setConvertResult({
        amount,
        fromCurrency: convertFrom,
        toCurrency: convertTo,
        result: amount,
        isDirect: true,
        fromRate: 1,
        toRate: 1,
        effectiveDate: convertDate,
        plnAmount: null
      });
      return;
    }

    setConvertLoading(true);
    setConvertResult(null);

    try {
      // Fetch rates for both currencies
      const [fromRateInfo, toRateInfo] = await Promise.all([
        fetchRateForCurrency(convertFrom, convertDate),
        fetchRateForCurrency(convertTo, convertDate)
      ]);

      if (!fromRateInfo) {
        toast.error(t('budget.couldNotFetchRateFor', { currency: convertFrom }));
        setConvertLoading(false);
        return;
      }

      if (!toRateInfo) {
        toast.error(t('budget.couldNotFetchRateFor', { currency: convertTo }));
        setConvertLoading(false);
        return;
      }

      // Convert: Source -> PLN -> Target
      // Rate is "PLN per 1 unit of currency"
      // So: PLN amount = source amount * source rate
      // Target amount = PLN amount / target rate

      const plnAmount = amount * fromRateInfo.rate;
      const targetAmount = plnAmount / toRateInfo.rate;

      // Check if it's a direct conversion (one of them is PLN)
      const isDirect = convertFrom === 'PLN' || convertTo === 'PLN';

      setConvertResult({
        amount,
        fromCurrency: convertFrom,
        toCurrency: convertTo,
        result: targetAmount,
        isDirect,
        fromRate: fromRateInfo.rate,
        toRate: toRateInfo.rate,
        fromRateDate: fromRateInfo.effectiveDate,
        toRateDate: toRateInfo.effectiveDate,
        fromRateNo: fromRateInfo.no,
        toRateNo: toRateInfo.no,
        plnAmount: isDirect ? null : plnAmount,
        requestedDate: convertDate
      });

    } catch (error) {
      toast.error(t('budget.conversionFailed', { error: error.message }));
    } finally {
      setConvertLoading(false);
    }
  }, [convertAmount, convertFrom, convertTo, convertDate, fetchRateForCurrency]);

  // Swap currencies in converter
  const swapCurrencies = () => {
    setConvertFrom(convertTo);
    setConvertTo(convertFrom);
    setConvertResult(null);
  };

  // Reset converter
  const resetConverter = () => {
    setConvertAmount('');
    setConvertResult(null);
    setConvertDate(new Date().toISOString().split('T')[0]);
  };

  // Reset Net to Gross calculator
  const resetNetToGross = () => {
    setNetAmount('');
    setCalculatedGross(null);
  };

  // Reset Gross to Net calculator
  const resetGrossToNet = () => {
    setGrossAmount('');
    setCalculatedNet(null);
  };

  // Reset exchange rate
  const resetExchangeRate = () => {
    setExchangeResult(null);
    setExchangeDate(new Date().toISOString().split('T')[0]);
  };

  // Handle Enter key press
  const handleKeyPress = (e, calculateFn) => {
    if (e.key === 'Enter') {
      calculateFn();
    }
  };

  // Format date for display
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(lang === 'pl' ? 'pl-PL' : 'en-GB', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Tax Rate Setting */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Calculator className="h-5 w-5" />
            {t('budget.taxRateSetting')}
          </CardTitle>
          <CardDescription>
            {t('budget.setTheTaxRateVatMwst')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 max-w-xs">
            <Label htmlFor="taxRate" className="whitespace-nowrap">{t('budget.taxRate')}</Label>
            <div className="relative flex-1">
              <Input
                id="taxRate"
                type="text"
                value={taxRate}
                onChange={(e) => setTaxRate(e.target.value)}
                className="pr-8"
                placeholder="23"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">%</span>
            </div>
          </div>
          <ResetFiltersButton testId="calculate-reset-defaults" className="mt-3 -ml-2" onClick={() => {
            setTaxRate('23'); setNetAmount(''); setGrossAmount(''); setCalculatedGross(null); setCalculatedNet(null);
            setSelectedCurrency('EUR'); setExchangeResult(null);
            setConvertAmount(''); setConvertFrom('EUR'); setConvertTo('PLN'); setConvertResult(null);
          }} />
        </CardContent>
      </Card>

      {/* Calculator Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Net to Gross Calculator */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <ArrowRight className="h-5 w-5 text-green-600" />
              {t('budget.netToGross')}
            </CardTitle>
            <CardDescription>
              {t('budget.calculateTheGrossAmountToPut')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="netAmount">{t('budget.netAmountBeforeTax')}</Label>
              <div className="flex gap-2">
                <Input
                  id="netAmount"
                  type="text"
                  value={netAmount}
                  onChange={(e) => setNetAmount(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, calculateNetToGross)}
                  placeholder={t('budget.enterNetAmount')}
                  className="flex-1"
                />
                <Button onClick={calculateNetToGross} data-testid="btn-calculate">
                  {t('budget.calculate')}
                </Button>
                <Button variant="outline" size="icon" onClick={resetNetToGross} title={t('budget.reset')} data-testid="btn-resetnettogross">
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {calculatedGross && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">{t('budget.netAmount')}</span>
                  <span className="flex items-center gap-1 font-medium">{formatCurrency(calculatedGross.net)}<CopyNumberButton value={calculatedGross.net} testId="copy-ntg-net" /></span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">{t('budget.taxWithRate', { rate: calculatedGross.rate })}</span>
                  <span className="flex items-center gap-1 font-medium text-orange-600">+ {formatCurrency(calculatedGross.tax)}<CopyNumberButton value={calculatedGross.tax} testId="copy-ntg-tax" /></span>
                </div>
                <div className="border-t border-green-300 pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-green-800">{t('budget.grossAmountInvoice')}</span>
                    <span className="flex items-center gap-1 text-xl font-bold text-green-700">{formatCurrency(calculatedGross.gross)}<CopyNumberButton value={calculatedGross.gross} testId="copy-ntg-gross" /></span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Gross to Net Calculator */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-lg">
              <ArrowLeft className="h-5 w-5 text-blue-600" />
              {t('budget.grossToNet')}
            </CardTitle>
            <CardDescription>
              {t('budget.calculateTheNetAmountFromA')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="grossAmount">{t('budget.grossAmountIncludingTax')}</Label>
              <div className="flex gap-2">
                <Input
                  id="grossAmount"
                  type="text"
                  value={grossAmount}
                  onChange={(e) => setGrossAmount(e.target.value)}
                  onKeyPress={(e) => handleKeyPress(e, calculateGrossToNet)}
                  placeholder={t('budget.enterGrossAmount')}
                  className="flex-1"
                />
                <Button onClick={calculateGrossToNet} data-testid="btn-calculate">
                  {t('budget.calculate')}
                </Button>
                <Button variant="outline" size="icon" onClick={resetGrossToNet} title={t('budget.reset')} data-testid="btn-resetgrosstonet">
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {calculatedNet && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">{t('budget.grossAmount')}</span>
                  <span className="flex items-center gap-1 font-medium">{formatCurrency(calculatedNet.gross)}<CopyNumberButton value={calculatedNet.gross} testId="copy-gtn-gross" /></span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">{t('budget.taxWithRate', { rate: calculatedNet.rate })}</span>
                  <span className="flex items-center gap-1 font-medium text-orange-600">- {formatCurrency(calculatedNet.tax)}<CopyNumberButton value={calculatedNet.tax} testId="copy-gtn-tax" /></span>
                </div>
                <div className="border-t border-blue-300 pt-2 mt-2">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-blue-800">{t('budget.netAmount')}</span>
                    <span className="flex items-center gap-1 text-xl font-bold text-blue-700">{formatCurrency(calculatedNet.net)}<CopyNumberButton value={calculatedNet.net} testId="copy-gtn-net" /></span>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* NBP Exchange Rate Tool */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Banknote className="h-5 w-5 text-purple-600" />
            {t('budget.nbpExchangeRate')}
          </CardTitle>
          <CardDescription>
            {t('budget.fetchTheNationalBankOfPoland')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Currency Selection */}
            <div className="space-y-2">
              <Label>{t('budget.currency')}</Label>
              <Select value={selectedCurrency} onValueChange={setSelectedCurrency}>
                <SelectTrigger>
                  <SelectValue placeholder={t('budget.selectCurrency')} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {CURRENCIES.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      <span className="font-mono font-medium">{currency.code}</span>
                      <span className="text-gray-500 ml-2">- {currencyName(currency)}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Selection */}
            <div className="space-y-2">
              <Label htmlFor="exchangeDate">{t('budget.referenceDate')}</Label>
              <Input
                id="exchangeDate"
                type="date"
                value={exchangeDate}
                onChange={(e) => setExchangeDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Action Buttons */}
            <div className="space-y-2 sm:col-span-2 lg:col-span-2">
              <Label className="invisible">{t('budget.actions')}</Label>
              <div className="flex gap-2">
                <Button 
                  onClick={fetchExchangeRate} 
                  disabled={exchangeLoading}
                  className="flex-1"
                 data-testid="btn-fetchexchangerate">
                  {exchangeLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('budget.fetching')}
                    </>
                  ) : (
                    <>
                      <Banknote className="h-4 w-4 mr-2" />
                      {t('budget.getRate')}
                    </>
                  )}
                </Button>
                <Button variant="outline" size="icon" onClick={resetExchangeRate} title={t('budget.reset')} data-testid="btn-resetexchangerate">
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Exchange Rate Result */}
          {exchangeResult && (
            <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-2xl font-bold text-purple-700">{exchangeResult.currency}</span>
                  <span className="text-gray-600 ml-2">({currencyName({ code: exchangeResult.currency, name: exchangeResult.currencyName })})</span>
                </div>
                <div className="text-right">
                  <div className="flex items-center justify-end gap-1 text-3xl font-bold text-purple-700">{formatRate(exchangeResult.rate)}<CopyNumberButton value={exchangeResult.rate} decimals={4} testId="copy-nbp-rate" /></div>
                  <div className="text-sm text-gray-500">{t('budget.plnPerOne', { currency: exchangeResult.currency })}</div>
                </div>
              </div>
              
              <div className="border-t border-purple-200 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">{t('budget.requestedDate')}</span>
                  <span className="ml-2 font-medium">{formatDate(exchangeResult.requestedDate)}</span>
                </div>
                <div>
                  <span className="text-gray-500">{t('budget.rateFrom')}</span>
                  <span className="ml-2 font-medium">{formatDate(exchangeResult.effectiveDate)}</span>
                </div>
                <div>
                  <span className="text-gray-500">{t('budget.nbpTable')}</span>
                  <span className="ml-2 font-medium">{exchangeResult.no}</span>
                </div>
                <div>
                  <span className="text-gray-500">{t('budget.tableType')}</span>
                  <span className="ml-2 font-medium">{exchangeResult.table}</span>
                </div>
              </div>
              
              <div className="border-t border-purple-200 pt-3">
                <p className="text-xs text-gray-500">{t('budget.nbpAverageRateNote')}</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Currency Converter */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <ArrowRightLeft className="h-5 w-5 text-teal-600" />
            {t('budget.currencyConverter2')}
          </CardTitle>
          <CardDescription>
            {t('budget.convertAmountsBetweenCurrenciesUsingNbp')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
            {/* Amount Input */}
            <div className="space-y-2 lg:col-span-1">
              <Label htmlFor="convertAmount">{t('budget.amount')}</Label>
              <Input
                id="convertAmount"
                type="text"
                value={convertAmount}
                onChange={(e) => setConvertAmount(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && convertCurrency()}
                placeholder={t('budget.enterAmount')}
              />
            </div>

            {/* From Currency */}
            <div className="space-y-2 lg:col-span-1">
              <Label>{t('budget.from2')}</Label>
              <Select value={convertFrom} onValueChange={setConvertFrom}>
                <SelectTrigger>
                  <SelectValue placeholder={t('budget.fromCurrency')} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {ALL_CURRENCIES.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      <span className="font-mono font-medium">{currency.code}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Swap Button */}
            <div className="flex items-center justify-center lg:col-span-1">
              <Button 
                variant="outline" 
                size="icon" 
                onClick={swapCurrencies} 
                title={t('budget.swapCurrencies')}
                className="rounded-full"
               data-testid="btn-swapcurrencies">
                <ArrowRightLeft className="h-4 w-4" />
              </Button>
            </div>

            {/* To Currency */}
            <div className="space-y-2 lg:col-span-1">
              <Label>{t('budget.to2')}</Label>
              <Select value={convertTo} onValueChange={setConvertTo}>
                <SelectTrigger>
                  <SelectValue placeholder={t('budget.toCurrency')} />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {ALL_CURRENCIES.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      <span className="font-mono font-medium">{currency.code}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Selection */}
            <div className="space-y-2 lg:col-span-1">
              <Label htmlFor="convertDate">{t('budget.date')}</Label>
              <Input
                id="convertDate"
                type="date"
                value={convertDate}
                onChange={(e) => setConvertDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
              />
            </div>

            {/* Action Buttons */}
            <div className="space-y-2 lg:col-span-1">
              <Label className="invisible hidden lg:block">{t('budget.actions')}</Label>
              <div className="flex gap-2">
                <Button 
                  onClick={convertCurrency} 
                  disabled={convertLoading}
                  className="flex-1"
                 data-testid="btn-convertcurrency">
                  {convertLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t('budget.converting')}
                    </>
                  ) : (
                    t('budget.convert')
                  )}
                </Button>
                <Button variant="outline" size="icon" onClick={resetConverter} title={t('budget.reset')} data-testid="btn-resetconverter">
                  <RotateCcw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          {/* Conversion Result */}
          {convertResult && (
            <div className="mt-4 p-4 bg-teal-50 border border-teal-200 rounded-lg space-y-3">
              {/* Main Result */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
                <div>
                  <div className="flex items-center gap-1 text-2xl font-bold text-gray-800">
                    {formatCurrency(convertResult.amount)} {convertResult.fromCurrency}
                    <CopyNumberButton value={convertResult.amount} testId="copy-convert-amount" />
                  </div>
                </div>
                <ArrowRight className="h-6 w-6 text-teal-600" />
                <div>
                  <div className="flex items-center gap-1 text-3xl font-bold text-teal-700">
                    {formatCurrency(convertResult.result)} {convertResult.toCurrency}
                    <CopyNumberButton value={convertResult.result} testId="copy-convert-result" />
                  </div>
                </div>
              </div>

              {/* Intermediate PLN result for non-direct conversions */}
              {!convertResult.isDirect && convertResult.plnAmount && (
                <div className="border-t border-teal-200 pt-3">
                  <div className="text-center text-sm text-gray-600 mb-2">
                    <span className="font-medium">{t('budget.conversionViaPlnNoDirectRate')}</span>
                  </div>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-2 text-sm">
                    <span className="font-mono">{formatCurrency(convertResult.amount)} {convertResult.fromCurrency}</span>
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                    <span className="flex items-center font-mono font-medium text-amber-700">{formatCurrency(convertResult.plnAmount)} PLN<CopyNumberButton value={convertResult.plnAmount} testId="copy-convert-pln" /></span>
                    <ArrowRight className="h-4 w-4 text-gray-400" />
                    <span className="font-mono font-medium text-teal-700">{formatCurrency(convertResult.result)} {convertResult.toCurrency}</span>
                  </div>
                </div>
              )}

              {/* Exchange Rates Used */}
              <div className="border-t border-teal-200 pt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {convertResult.fromCurrency !== 'PLN' && (
                  <div className="p-2 bg-white rounded border">
                    <div className="font-medium text-gray-700">{t('budget.rateToPln', { currency: convertResult.fromCurrency })}</div>
                    <div className="flex items-center gap-1 text-lg font-bold">{formatRate(convertResult.fromRate)}<CopyNumberButton value={convertResult.fromRate} decimals={4} testId="copy-convert-from-rate" /></div>
                    <div className="text-xs text-gray-500">
                      NBP {convertResult.fromRateNo} • {formatDate(convertResult.fromRateDate)}
                    </div>
                  </div>
                )}
                {convertResult.toCurrency !== 'PLN' && (
                  <div className="p-2 bg-white rounded border">
                    <div className="font-medium text-gray-700">{t('budget.rateToPln', { currency: convertResult.toCurrency })}</div>
                    <div className="flex items-center gap-1 text-lg font-bold">{formatRate(convertResult.toRate)}<CopyNumberButton value={convertResult.toRate} decimals={4} testId="copy-convert-to-rate" /></div>
                    <div className="text-xs text-gray-500">
                      NBP {convertResult.toRateNo} • {formatDate(convertResult.toRateDate)}
                    </div>
                  </div>
                )}
              </div>

              <div className="text-xs text-gray-500 text-center">
                {t('budget.ratesForWorkingDayBefore', { date: formatDate(convertResult.requestedDate) })}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Reference */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">{t('budget.quickReference')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-600 space-y-2">
            <p><strong>{t('budget.netToGrossFormula')}</strong> {t('budget.grossNet1TaxRate100')}</p>
            <p><strong>{t('budget.grossToNetFormula')}</strong> {t('budget.netGross1TaxRate100')}</p>
            <p className="text-xs text-gray-500 mt-3">
              {t('budget.tipYouCanUseEitherPeriod')}
            </p>
            <div className="border-t pt-3 mt-3">
              <p><strong>{t('budget.nbpExchangeRates')}</strong> {t('budget.nbpExchangeRatesHelp')}</p>
            </div>
            <div className="border-t pt-3 mt-3">
              <p><strong>{t('budget.currencyConverter')}</strong> {t('budget.currencyConverterHelp')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TaxCalculator;
